import { Link } from "@tanstack/react-router";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/cn";
import { formatDate } from "../lib/date";
import { DEMO_DATASET, type DemoDataset } from "../lib/demoDataset";
import { formatMoney } from "../lib/money";

// Read-only demo view (L0-U10 session 2, ADR 0031). Renders the bundled
// DEMO_DATASET directly — it issues NO tenant queries or mutations, so demo
// content is structurally isolated from the real account. Reachable from the
// first-run wizard ("Explore demo data") and Settings -> Getting started.

function Sparkline({ points }: { points: readonly number[] }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const stepX = 100 / (points.length - 1);
  const path = points
    .map((value, index) => {
      const x = index * stepX;
      // Invert y: SVG y grows downward, equity should rise upward.
      const y = 30 - ((value - min) / span) * 30;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      role="img"
      aria-label="Demo equity curve trending up"
      className="h-24 w-full"
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.2}
        className="text-primary"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
          {label}
        </p>
        <p className="mt-1 font-display text-2xl font-medium tracking-tight text-on-surface">
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export function DemoPage({
  dataset = DEMO_DATASET,
}: {
  dataset?: DemoDataset;
}) {
  const { summary, equityCurve, trades } = dataset;

  return (
    <section className="space-y-6 p-6">
      <div
        role="status"
        data-testid="demo-banner"
        className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-outline-variant bg-surface-container-low px-4 py-3"
      >
        <p className="text-sm text-on-surface-variant">
          <span className="font-medium text-on-surface">
            You&rsquo;re viewing demo data.
          </span>{" "}
          Nothing here is saved to your account &mdash; it&rsquo;s a sample so you
          can see what a populated journal looks like.
        </p>
        <Link
          to="/dashboard"
          className="rounded-control bg-primary px-3 py-1.5 text-sm font-medium text-on-primary transition-colors hover:bg-primary-hover"
        >
          Exit demo
        </Link>
      </div>

      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Demo dashboard
        </h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          A snapshot of a sample trader&rsquo;s recent month.
        </p>
      </header>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Metric label="Net P&L" value={formatMoney(summary.netPnl)} />
        <Metric
          label="Win rate"
          value={`${Math.round(summary.winRate * 100)}%`}
        />
        <Metric
          label="Profit factor"
          value={summary.profitFactor.toFixed(2)}
        />
        <Metric label="Trades" value={String(summary.totalTrades)} />
        <Metric label="Avg win" value={formatMoney(summary.avgWin)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Equity curve</CardTitle>
        </CardHeader>
        <CardContent>
          <Sparkline points={equityCurve} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent trades</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Symbol</TableHead>
                <TableHead>Side</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead className="text-right">R</TableHead>
                <TableHead className="text-right">P&L</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trades.map((trade) => {
                const isWin = !trade.pnl.startsWith("-");
                return (
                  <TableRow key={trade.id}>
                    <TableCell className="font-medium">{trade.symbol}</TableCell>
                    <TableCell className="capitalize">{trade.side}</TableCell>
                    <TableCell>{formatDate(trade.openedOn)}</TableCell>
                    <TableCell>{formatDate(trade.closedOn)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {trade.rMultiple.toFixed(1)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums",
                        isWin ? "text-success" : "text-danger",
                      )}
                    >
                      {formatMoney(trade.pnl)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}
